import { useState } from 'react';
import axios from 'axios';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-[#0a0a0a] to-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16" data-testid="contact-header">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Get In <span className="gold-gradient">Touch</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Have questions? Need a quote? We're here to help!
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div className="bg-[#1a1a1a] rounded-xl p-8 border border-gray-800">
              <h3 className="text-2xl font-bold mb-6 gold-gradient">Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start" data-testid="contact-phone">
                  <Phone className="w-6 h-6 text-[#d4af37] mt-1 mr-4 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-white">Phone</p>
                    <a href="tel:4038014638" className="text-gray-400 hover:text-[#d4af37] transition">
                      (403) 801-4638
                    </a>
                  </div>
                </div>

                <div className="flex items-start" data-testid="contact-email">
                  <Mail className="w-6 h-6 text-[#d4af37] mt-1 mr-4 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-white">Email</p>
                    <a href="mailto:yychauling@gmail.com" className="text-gray-400 hover:text-[#d4af37] transition">
                      yychauling@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start" data-testid="contact-location">
                  <MapPin className="w-6 h-6 text-[#d4af37] mt-1 mr-4 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-white">Service Area</p>
                    <p className="text-gray-400">Calgary & Surrounding Areas</p>
                  </div>
                </div>

                <div className="flex items-start" data-testid="contact-hours">
                  <Clock className="w-6 h-6 text-[#d4af37] mt-1 mr-4 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-white">Hours</p>
                    <p className="text-gray-400">Open 24 Hours</p>
                    <p className="text-sm text-gray-500">Available for emergencies and last-minute requests</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#d4af37]/10 rounded-xl p-6 border border-[#d4af37]/30">
              <h4 className="font-bold text-lg mb-2 text-[#d4af37]">Why Choose Us?</h4>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li>✓ Locally owned and operated in Calgary</li>
                <li>✓ Fully registered and insured</li>
                <li>✓ Professional fleet with multiple vehicle options</li>
                <li>✓ Competitive pricing with no hidden fees</li>
                <li>✓ Reliable and punctual service</li>
                <li>✓ Experienced team handling residential & commercial projects</li>
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-[#1a1a1a] rounded-xl p-8 border border-gray-800">
            <h3 className="text-2xl font-bold mb-6 text-white">Send Us a Message</h3>
            
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:border-[#d4af37] focus:outline-none text-white"
                  placeholder="Your name"
                  data-testid="contact-name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:border-[#d4af37] focus:outline-none text-white"
                  placeholder="your@email.com"
                  data-testid="contact-form-email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Phone *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:border-[#d4af37] focus:outline-none text-white"
                  placeholder="(403) 555-0123"
                  data-testid="contact-form-phone"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Message *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:border-[#d4af37] focus:outline-none text-white resize-none"
                  placeholder="Tell us about your needs..."
                  data-testid="contact-message"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#d4af37] text-black px-6 py-4 rounded-lg font-bold hover:bg-[#b8941f] transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                data-testid="submit-contact"
              >
                {isSubmitting ? 'Sending...' : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;